import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, ArrowLeft, ArrowRight, ExternalLink, Ruler, Maximize, Home, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';
import { getOrganizationSchema, getBreadcrumbSchema } from '../seo/schemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== CITY LIST PAGE =====
const ADURegulations = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const navigate = useNavigate();
  const [regulations, setRegulations] = useState([]);
  const [banner, setBanner] = useState({ title: '2026 ADU Regulations', subtitle: 'City-by-city ADU building regulations guide for California', images: [] });
  const [slideIdx, setSlideIdx] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const [regsRes, bannerRes] = await Promise.all([
          axios.get(`${API}/admin/regulations`),
          axios.get(`${API}/admin/settings/reg_banner`),
        ]);
        setRegulations(regsRes.data.regulations);
        if (bannerRes.data?.value?.title) setBanner(bannerRes.data.value);
      } catch {}
    };
    load();
  }, []);

  // Auto-rotate banner images
  const bannerImages = banner.images?.length > 0 ? banner.images : (banner.image ? [banner.image] : []);
  useEffect(() => {
    if (bannerImages.length <= 1) return;
    const interval = setInterval(() => setSlideIdx(prev => (prev + 1) % bannerImages.length), 5000);
    return () => clearInterval(interval);
  }, [bannerImages.length]);

  const cities = regulations;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        title={isZh ? '加州 ADU 法规 2026 | 各城市 ADU 申请条件、退线、面积一览 | ALIGCUS' : 'California ADU Regulations 2026 | City-by-City ADU Permit Rules, Setbacks & Limits | ALIGCUS'}
        description={isZh ? '查询加州各市最新 ADU 法规：洛杉矶、帕萨迪纳、阿凯迪亚、圣盖博、戴蒙德吧等。退线要求、最大面积、特殊规定一目了然。免费咨询 (626) 803-8000。' : 'California city-by-city ADU regulations 2026: Los Angeles, Pasadena, Arcadia, San Gabriel, Diamond Bar and more. Setbacks, max sqft, special requirements at a glance. Free consultation (626) 803-8000.'}
        canonical={isZh ? 'https://www.aligcus.com/zh/adu-regulations' : 'https://www.aligcus.com/adu-regulations'}
        lang={isZh ? 'zh' : 'en'}
        alternates={[
          { lang: 'en', url: 'https://www.aligcus.com/adu-regulations' },
          { lang: 'zh-CN', url: 'https://www.aligcus.com/zh/adu-regulations' },
        ]}
        schemas={[
          getOrganizationSchema(isZh ? 'zh' : 'en'),
          getBreadcrumbSchema([
            { name: isZh ? '首页' : 'Home', url: isZh ? 'https://www.aligcus.com/zh/' : 'https://www.aligcus.com/' },
            { name: isZh ? 'ADU 法规' : 'ADU Regulations', url: isZh ? 'https://www.aligcus.com/zh/adu-regulations' : 'https://www.aligcus.com/adu-regulations' },
          ]),
        ]}
      />
      {/* Banner Carousel */}
      <section className="relative flex items-center justify-center overflow-hidden" style={{ minHeight: '300px', maxHeight: '60vh' }}>
        <div className="absolute inset-0 bg-[#4a6fa5]">
          {bannerImages.map((img, i) => (
            <img key={i} src={img} alt="" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === slideIdx % bannerImages.length ? 'opacity-80' : 'opacity-0'}`} />
          ))}
        </div>
        <div className="relative text-center px-4 sm:px-6 py-12 sm:py-0">
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">{isZh ? (banner.title_zh || banner.title) : (banner.title_en || banner.title)}</h1>
          <p className="text-sm sm:text-xl md:text-2xl text-white/80 font-light">{isZh ? (banner.subtitle_zh || banner.subtitle) : (banner.subtitle_en || banner.subtitle)}</p>
        </div>
        {bannerImages.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {bannerImages.map((_, i) => (
              <button key={i} onClick={() => setSlideIdx(i)} className={`h-2 rounded-full transition-all ${i === slideIdx % bannerImages.length ? 'w-8 bg-white' : 'w-2 bg-white/50'}`} />
            ))}
          </div>
        )}
      </section>

      {/* Region Selection */}
      <main className="flex-grow py-12">
        <div className="max-w-5xl mx-auto px-6">
          <RegionView regulations={regulations} />
        </div>
      </main>
      <Footer />
    </div>
  );
};

// ===== REGION VIEW — North/South → County → City =====
const RegionView = ({ regulations }) => {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  if (!regulations || regulations.length === 0) return <p className="text-center text-gray-400 py-8">Loading...</p>;

  // Group by region → county → cities
  const northRegs = regulations.filter(r => r.region === 'North');
  const southRegs = regulations.filter(r => r.region === 'South');
  const sdRegs = regulations.filter(r => r.region === 'San Diego');
  const unassigned = regulations.filter(r => !r.region || !['North','South','San Diego'].includes(r.region));

  const groupByCounty = (regs) => {
    const map = {};
    regs.forEach(r => {
      const county = r.county || 'Other';
      if (!map[county]) map[county] = [];
      map[county].push(r);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  };

  const currentRegs = selectedRegion === 'North' ? northRegs : selectedRegion === 'South' ? southRegs : selectedRegion === 'San Diego' ? sdRegs : null;
  const countyGroups = currentRegs ? groupByCounty(currentRegs) : null;

  return (
    <div>
      {/* Region Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-10">
        {[
          { key: 'South', label: 'Southern California', labelZh: '南加州', color: 'from-red-600 to-red-800' },
          { key: 'North', label: 'Northern California', labelZh: '北加州', color: 'from-red-600 to-red-800' },
          { key: 'San Diego', label: 'San Diego', labelZh: '圣地亚哥', color: 'from-red-600 to-red-800', direct: true },
        ].map(region => (
          <button key={region.key} onClick={() => {
            if (region.direct) { const sd = regulations.find(r => r.region === 'San Diego'); if (sd) { navigate(`/adu-regulations/${encodeURIComponent(sd.city)}`); } return; }
            setSelectedRegion(selectedRegion === region.key ? null : region.key);
          }}
            className={`relative overflow-hidden rounded-2xl p-6 sm:p-8 text-left transition-all duration-300 hover:scale-[1.02] ${selectedRegion === region.key ? 'ring-4 ring-red-400 shadow-2xl' : 'shadow-lg hover:shadow-xl'}`}>
            <div className={`absolute inset-0 bg-gradient-to-br ${region.color}`} />
            <div className="relative text-white">
              <h3 className="text-lg sm:text-2xl font-bold">{isZh ? region.labelZh : region.label}</h3>
            </div>
          </button>
        ))}
      </div>

      {/* County → City List */}
      {countyGroups && (
        <div className="space-y-8">
          {countyGroups.map(([county, cities]) => (
            <div key={county}>
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <div className="w-2 h-8 bg-red-600 rounded-full" />
                {county}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {cities.map(reg => (
                  <div key={reg.id} className="relative">
                    <Link to={`/adu-regulations/${encodeURIComponent(reg.city)}`}
                      className="flex items-center gap-3 px-4 py-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors group">
                      <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <span className="text-gray-800 font-semibold text-lg group-hover:text-gray-900">{reg.city}</span>
                    </Link>
                    {reg.pre_approved && (
                      <a href="https://mp.weixin.qq.com/s/zu4TpjcjKzN1yBH56RUGUg?scene=1&click_id=2" target="_blank" rel="noopener noreferrer"
                        className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center text-xs shadow hover:scale-110 transition-transform" title={isZh ? '已通过预审批' : 'Pre-Approved'}>
                        🕒
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Unassigned cities (no region set) */}
      {!selectedRegion && unassigned.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-gray-500 mb-3">All Cities</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {unassigned.map(reg => (
              <div key={reg.id} className="relative">
                <Link to={`/adu-regulations/${encodeURIComponent(reg.city)}`}
                  className="flex items-center gap-3 px-4 py-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors group">
                  <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-gray-800 font-semibold text-lg group-hover:text-gray-900">{reg.city}</span>
                </Link>
                {reg.pre_approved && (
                  <a href="https://mp.weixin.qq.com/s/zu4TpjcjKzN1yBH56RUGUg?scene=1&click_id=2" target="_blank" rel="noopener noreferrer"
                    className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center text-xs shadow hover:scale-110 transition-transform">🕒</a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ===== CITY DETAIL PAGE =====
export const ADURegulationDetail = () => {
  const { id } = useParams();
  const cityName = decodeURIComponent(id);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [reg, setReg] = useState(null);
  const [allRegs, setAllRegs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/regulations`);
        const regs = r.data.regulations;
        setAllRegs(regs);
        setReg(regs.find(r => r.city === cityName || r.id === cityName));
      } catch {}
      finally { setLoading(false); }
    };
    load();
  }, [cityName]);

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20"><div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!reg) return <div className="min-h-screen flex items-center justify-center pt-20"><h1 className="text-2xl font-bold">Not Found</h1></div>;

  const currentIdx = allRegs.findIndex(r => r.city === cityName || r.id === cityName);
  const nextReg = currentIdx >= 0 && currentIdx < allRegs.length - 1 ? allRegs[currentIdx + 1] : null;

  return (
    <div className="min-h-screen bg-white flex flex-col pt-20">
      <main className="flex-grow">
        <div className="w-full px-4 py-10">
          <div className="flex gap-6 justify-center">
            {/* Left Sticky Images */}
            {((Array.isArray(reg.image1) ? reg.image1 : (reg.image1 ? [reg.image1] : [])).filter(x => x)).length > 0 && (
              <div className="hidden lg:block flex-shrink-0" style={{ width: '400px' }}>
                <div className="sticky top-24 space-y-4">
                  {(Array.isArray(reg.image1) ? reg.image1 : [reg.image1]).map((img, i) => (
                    <div key={i} className="rounded-xl overflow-hidden shadow-md" style={{ width: '400px' }}>
                      <img src={img} alt={reg.city} className="w-full" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Center Content */}
            <div className="flex-1 min-w-0" style={{ maxWidth: '900px' }}>
              {/* Content — HTML or structured data */}
              <div className="prose max-w-none mb-8">
            {reg.content_html ? (
              <div dangerouslySetInnerHTML={{ __html: isZh ? (reg.content_html_zh || reg.content_html) : (reg.content_html_en || reg.content_html) }} />
            ) : (
              <div className="space-y-4">
                {(isZh ? reg.setbacks_zh : reg.setbacks_en) || reg.setbacks ? <div className="flex items-start gap-3"><Ruler className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" /><div><h3 className="font-bold text-gray-900">{isZh ? '退距' : 'Setbacks'}</h3><p className="text-gray-700">{isZh ? (reg.setbacks_zh || reg.setbacks) : (reg.setbacks_en || reg.setbacks)}</p></div></div> : null}
                {reg.max_sqft && <div className="flex items-start gap-3"><Maximize className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" /><div><h3 className="font-bold text-gray-900">{isZh ? 'ADU最大面积' : 'Max ADU Size'}</h3><p className="text-gray-700">{reg.max_sqft} sq.ft</p></div></div>}
                {reg.lot_coverage && <div className="flex items-start gap-3"><Home className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" /><div><h3 className="font-bold text-gray-900">Lot Coverage</h3><p className="text-gray-700">{reg.lot_coverage}</p></div></div>}
                {(isZh ? reg.special_req_zh : reg.special_req_en) || reg.special_req ? <div className="flex items-start gap-3"><AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" /><div><h3 className="font-bold text-gray-900">{isZh ? '特殊要求' : 'Special Requirements'}</h3><p className="text-gray-700 whitespace-pre-line">{isZh ? (reg.special_req_zh || reg.special_req) : (reg.special_req_en || reg.special_req)}</p></div></div> : null}
                {reg.regulations && <div className="flex items-start gap-3"><FileText className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" /><div><h3 className="font-bold text-gray-900">{isZh ? '法规' : 'Regulations'}</h3><p className="text-gray-700">{reg.regulations}</p></div></div>}
                {reg.links?.length > 0 && <div className="pt-2 space-y-1">{(reg.links || []).filter(l => l && typeof l === 'object').map((l, i) => <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-red-600 hover:text-red-700 text-sm font-medium"><ExternalLink className="w-3.5 h-3.5" />{l.label || 'View Code'}</a>)}</div>}
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center pt-8 border-t border-gray-200">
            <Button variant="outline" onClick={() => navigate('/adu-regulations')} className="border-gray-300">
              <ArrowLeft className="w-4 h-4 mr-2" />{isZh ? '返回上一级' : 'Back'}
            </Button>
            {nextReg && (
              <Button onClick={() => navigate(`/adu-regulations/${encodeURIComponent(nextReg.city)}`)} className="bg-red-600 hover:bg-red-700 text-white">
                {nextReg.city} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
            </div>

            {/* Right Sticky Images + Regulations Link */}
            <div className="hidden lg:block flex-shrink-0" style={{ width: '400px' }}>
              <div className="sticky top-24 space-y-4">
                {(Array.isArray(reg.image2) ? reg.image2 : (reg.image2 ? [reg.image2] : [])).filter(x => x).map((img, i) => (
                  <div key={i} className="rounded-xl overflow-hidden shadow-md" style={{ width: '400px' }}>
                    <img src={img} alt={reg.city} className="w-full" />
                  </div>
                ))}
                {reg.regulations && reg.regulations.startsWith('http') && (
                  <a href={reg.regulations} target="_blank" rel="noopener noreferrer" className="block bg-white rounded-xl shadow-md border p-4 hover:shadow-lg transition-shadow">
                    <p className="text-xs text-gray-400 mb-1">{isZh ? '详情来源于' : 'Source'}</p>
                    <p className="text-sm text-red-600 font-medium break-all hover:underline">{reg.regulations}</p>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ADURegulations;
